// frontend/src/components/common/AppMonthRangeSelect.jsx
//
// Two bounded `Select` dropdowns for a month range. The option list is
// derived from the store's data coverage (`minAllowed`..`maxAllowed`), so
// users can only pick months where data exists and can't violate the
// backend's 24-month MAX_MONTHS cap. Visually identical to the Location
// and Group filters (same MUI `Select size="small"`), so reports using
// this look consistent with the rest of the filter bar.
//
// Values in/out are 'YYYY-MM' strings — same contract as the month variant
// of AppDateRangePicker, so swapping between the two is a drop-in.

import { useMemo } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useFormatters } from '../../hooks/useFormatters';
import { monthsBetween, subMonths } from '../../utils/monthUtils';

// How many months of history to expose in the dropdown. The backend caps a
// single request at 24 months (MAX_MONTHS in gmroi_reporting.py), but we
// show 12 so the options stay scannable. Overridable via the `historyMonths`
// prop when a caller needs a shorter/longer window.
const DEFAULT_HISTORY_MONTHS = 12;

function AppMonthRangeSelect({
  startValue,
  onStartChange,
  endValue,
  onEndChange,
  minAllowed,           // 'YYYY-MM' floor (earliest month with data)
  maxAllowed,           // 'YYYY-MM' ceiling (today's month or last complete)
  historyMonths = DEFAULT_HISTORY_MONTHS,
  startLabel = 'From month',
  endLabel = 'To month',
  width = 150,
  gap = 2,
  sx,
}) {
  const { formatMonthYear } = useFormatters();

  // The option list is always the last `historyMonths` ending at maxAllowed,
  // clamped to whatever data actually exists. If the store has 5 years of
  // history we still only surface the most recent year — picking Mar 2022
  // via dropdown isn't a real workflow.
  const allMonths = useMemo(() => {
    if (!maxAllowed) return [];
    const windowStart = subMonths(maxAllowed, historyMonths - 1, minAllowed);
    const effectiveFloor = minAllowed && windowStart && minAllowed > windowStart
      ? minAllowed
      : windowStart;
    return monthsBetween(effectiveFloor, maxAllowed);
  }, [minAllowed, maxAllowed, historyMonths]);

  // Cross-constraints: a selection on one side narrows the other side so
  // `start <= end` always holds. The `historyMonths` window already
  // guarantees the range fits within the backend cap, so no extra width
  // check is needed here.
  const startOptions = useMemo(() => {
    if (!allMonths.length) return [];
    const ceiling = endValue || allMonths[allMonths.length - 1];
    return allMonths.filter((m) => m <= ceiling);
  }, [allMonths, endValue]);

  const endOptions = useMemo(() => {
    if (!allMonths.length) return [];
    const floor = startValue || allMonths[0];
    return allMonths.filter((m) => m >= floor);
  }, [allMonths, startValue]);

  // Contract: the consumer owns the range values and is responsible for
  // keeping them inside the currently-valid option set. If a stale value is
  // passed (e.g. bounds shifted under the caller), the Select renders empty
  // so the desync is visible — we never self-correct here because writing
  // back from a child effect races any parent-side persistence.
  const safeStart = startOptions.includes(startValue) ? startValue : '';
  const safeEnd = endOptions.includes(endValue) ? endValue : '';

  const renderLabel = (ym) => (ym ? formatMonthYear(ym) : '');

  return (
    <Box sx={{ display: 'flex', gap, alignItems: 'center', flexWrap: 'wrap', ...sx }}>
      <FormControl size="small" sx={{ width }}>
        <InputLabel>{startLabel}</InputLabel>
        <Select
          value={safeStart}
          label={startLabel}
          onChange={(e) => onStartChange?.(e.target.value)}
        >
          {startOptions.map((m) => (
            <MenuItem key={m} value={m}>{renderLabel(m)}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ width }}>
        <InputLabel>{endLabel}</InputLabel>
        <Select
          value={safeEnd}
          label={endLabel}
          onChange={(e) => onEndChange?.(e.target.value)}
        >
          {endOptions.map((m) => (
            <MenuItem key={m} value={m}>{renderLabel(m)}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export default AppMonthRangeSelect;
